import { LeftPanelType, ViewPart, Visibility } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";

/**
 * Whether a left-panel rail button should paint itself active. `getActiveLeftPanel()` names the
 * panel a dashboard state last clicked, but that panel may not be on screen at all: a resident
 * panel (UI Design, Report Designer) can be collapsed, and an on-demand one (Scheduled Jobs, the
 * API Builder, the workflow editor) may never have been opened. `ViewPart.LeftPanel`'s own
 * visibility is the single source of truth for "is anything showing there right now", so a
 * button is active only when both agree.
 *
 * Every rail button's `active` prop — and the shared sliding indicator it drives
 * (`KuikaSelectionIndicator`, keyed off the `.kuika-button__active` class it toggles) — must read
 * this the same way. Comparing `getActiveLeftPanel()` alone paints a button active, and slides the
 * indicator onto it, while its panel is actually closed.
 */
export const isLeftPanelButtonActive = (sd: IScreenDesignContextState, panelType: LeftPanelType): boolean =>
  sd.getActiveLeftPanel() === panelType && sd.getVisibility(ViewPart.LeftPanel) === Visibility.Visible;

/**
 * The rail's click contract for a left-panel button: a second click on the panel that is already
 * open and showing closes it, exactly like the panel's own edge collapse button; any other click
 * opens it, expanding a collapsed resident panel first. Shared by every rail button through
 * `DesignLeftMenu.handleMenuItemClick`, so the decision is made once, the same way, on every
 * dashboard state that hosts a left-panel rail button.
 *
 * The close path goes through `collapseLeftPanel` rather than `setLeftPanelCollapsed` directly:
 * the workflow editor's on-demand datasources panel closes by clearing the active panel, never the
 * collapse flag (see `DesignSituationProvider.collapseLeftPanel`), and `collapseLeftPanel` is what
 * already routes between that and the collapse flag every resident panel uses. Reaching for
 * `setActiveLeftPanelByClick` here instead would silently no-op: it already returns early when the
 * clicked panel is the active one (see its own comment), which is exactly why a second rail click
 * used to do nothing.
 */
export const handleLeftPanelRailClick = (sd: IScreenDesignContextState, panelType: LeftPanelType): void => {
  if (isLeftPanelButtonActive(sd, panelType)) {
    sd.collapseLeftPanel();
    return;
  }

  if (sd.isLeftPanelCollapsed[sd.dashboardState]) {
    sd.setLeftPanelCollapsed(false);
  }

  sd.setActiveLeftPanelByClick(panelType);
};
